import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { Variables } from 'graphql-request';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

import { getDocumentName, getFragmentData } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';
import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';

import { Access_Answer_Fragment } from '../../Access/fragments/access-answer.fragment';
import { gateOf } from '../../Access/utils/access-gate.util';
import { FileExplorer_FileCategoryAccess_Fragment } from '../fragments/file-category-access.fragment';
import type { FileCategory } from '../types/file-category.type';
import type { FileCategoryAccesses } from '../types/file-category-access.type';
import type { FileItemWithCategory } from '../types/file-item-with-category.type';
import type { Mode } from '../types/mode.type';

interface DeleteFilesVariables {
  readonly entityId: Id;
  readonly fileIds: readonly Id[];
}

interface MoveFilesVariables {
  readonly entityId: Id;
  readonly destinationFileCategoryId: Id;
  readonly fileIds: readonly Id[];
}

interface RenameFileVariables {
  readonly entityId: Id;
  readonly fileId: Id;
  readonly newOriginalName: string;
}

interface FileDirectorySelection {
  readonly allowedFileCategories: readonly FileCategory[];
  readonly fileCategoryAccesses: readonly FragmentType<
    typeof FileExplorer_FileCategoryAccess_Fragment
  >[];
  readonly files: readonly FileItemWithCategory[];
}

interface UseFilesPageStateParams<
  TQueryResult,
  TQueryVariables extends Variables,
> {
  readonly entityId: Id | null | undefined;
  readonly buildFilesPath: (
    fileCategoryId: Id,
    mode: Mode,
    fileId?: Id | null,
  ) => string;
  readonly queryDocument: TypedDocumentNode<TQueryResult, TQueryVariables>;
  readonly queryVariables: TQueryVariables;
  readonly selectFileDirectory: (data: TQueryResult) => FileDirectorySelection;
  readonly deleteFilesMutationDocument: TypedDocumentNode<
    unknown,
    DeleteFilesVariables
  >;
  readonly moveFilesMutationDocument: TypedDocumentNode<
    unknown,
    MoveFilesVariables
  >;
  readonly renameFileMutationDocument: TypedDocumentNode<
    unknown,
    RenameFileVariables
  >;
  readonly relatedQueryDocumentsToInvalidate: readonly TypedDocumentNode<
    unknown,
    never
  >[];
}

export function useFilesPageState<
  TQueryResult,
  TQueryVariables extends Variables,
>({
  entityId,
  buildFilesPath,
  queryDocument,
  queryVariables,
  selectFileDirectory,
  deleteFilesMutationDocument,
  moveFilesMutationDocument,
  renameFileMutationDocument,
  relatedQueryDocumentsToInvalidate,
}: Readonly<UseFilesPageStateParams<TQueryResult, TQueryVariables>>) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { fileCategoryId: urlFileCategoryId, mode: urlMode } = useParams<{
    fileCategoryId?: string;
    mode?: string;
  }>();

  const { data: fileDirectoryData, isLoading: isLoadingFileDirectory } =
    useGraphQLQuery(queryDocument, queryVariables, {
      enabled: !!entityId,
    });

  const fileDirectory = useMemo(
    () => (fileDirectoryData ? selectFileDirectory(fileDirectoryData) : null),
    [fileDirectoryData, selectFileDirectory],
  );

  const allowedFileCategories = useMemo(
    () => fileDirectory?.allowedFileCategories ?? [],
    [fileDirectory],
  );

  const fileCategoryAccesses: FileCategoryAccesses = useMemo(
    () =>
      new Map(
        getFragmentData(
          FileExplorer_FileCategoryAccess_Fragment,
          fileDirectory?.fileCategoryAccesses ?? [],
        ).map(({ fileCategoryId, download, upload }) => [
          fileCategoryId,
          {
            download: gateOf(getFragmentData(Access_Answer_Fragment, download)),
            upload: gateOf(getFragmentData(Access_Answer_Fragment, upload)),
          },
        ]),
      ),
    [fileDirectory],
  );

  const visibleFileCategories = useMemo(
    () =>
      allowedFileCategories.filter((category) => !category.hideInFileExplorer),
    [allowedFileCategories],
  );

  const allFiles = useMemo(() => fileDirectory?.files ?? [], [fileDirectory]);

  const fileCountsByCategoryId = useMemo(() => {
    const counts = new Map<Id, number>();

    for (const file of allFiles) {
      if (file.hideInFileExplorer) {
        continue;
      }
      counts.set(file.categoryId, (counts.get(file.categoryId) ?? 0) + 1);
    }

    return counts;
  }, [allFiles]);

  const selectedFileCategoryId: Id | null =
    (urlFileCategoryId as Id | undefined) ??
    visibleFileCategories[0]?._id ??
    null;

  const files = useMemo(
    () => allFiles.filter((file) => file.categoryId === selectedFileCategoryId),
    [allFiles, selectedFileCategoryId],
  );

  const validMode: Mode | null =
    urlMode === 'content' || urlMode === 'upload' || urlMode === 'gallery'
      ? urlMode
      : null;
  const validCategoryId =
    visibleFileCategories.find((category) => category._id === urlFileCategoryId)
      ?._id ?? null;
  const firstCategoryId = visibleFileCategories[0]?._id ?? null;

  const redirectTo =
    !isLoadingFileDirectory &&
    firstCategoryId !== null &&
    (!validCategoryId || !validMode)
      ? buildFilesPath(
          validCategoryId ?? firstCategoryId,
          validMode ?? 'content',
        )
      : null;

  const navigate = useNavigate();

  useEffect(() => {
    if (redirectTo) {
      void navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate]);

  const queryDocumentNamesToInvalidate = useMemo(
    () => [
      getDocumentName(queryDocument),
      ...relatedQueryDocumentsToInvalidate.map(getDocumentName),
    ],
    [queryDocument, relatedQueryDocumentsToInvalidate],
  );

  const invalidateFilesQueries = useCallback(() => {
    queryDocumentNamesToInvalidate.forEach((queryDocumentName) => {
      void queryClient.invalidateQueries({
        queryKey: [queryDocumentName],
      });
    });
  }, [queryClient, queryDocumentNamesToInvalidate]);

  const { mutate: deleteFilesMutation, isPending: isDeletingFiles } =
    useGraphQLMutation(deleteFilesMutationDocument);

  const handleDeleteFiles = useCallback(
    (fileIds: readonly Id[]) =>
      new Promise<void>((resolve, reject) => {
        deleteFilesMutation(
          { entityId: entityId ?? EMPTY_ID, fileIds },
          {
            onSuccess: () => {
              enqueueSnackbar('Fichiers supprimés.');
              invalidateFilesQueries();
              resolve();
            },
            onError: (mutationError: unknown) => {
              reject(
                mutationError instanceof Error
                  ? mutationError
                  : new Error('Une erreur est survenue.'),
              );
            },
          },
        );
      }),
    [entityId, deleteFilesMutation, enqueueSnackbar, invalidateFilesQueries],
  );

  const { mutate: moveFilesMutation, isPending: isMovingFiles } =
    useGraphQLMutation(moveFilesMutationDocument);

  const handleMoveFiles = useCallback(
    ({
      fileIds,
      destinationFileCategoryId,
    }: Readonly<{
      fileIds: readonly Id[];
      destinationFileCategoryId: Id;
    }>) =>
      new Promise<void>((resolve, reject) => {
        moveFilesMutation(
          {
            entityId: entityId ?? EMPTY_ID,
            destinationFileCategoryId,
            fileIds,
          },
          {
            onSuccess: () => {
              enqueueSnackbar('Fichiers déplacés.');
              invalidateFilesQueries();
              resolve();
            },
            onError: (mutationError: unknown) => {
              reject(
                mutationError instanceof Error
                  ? mutationError
                  : new Error('Une erreur est survenue.'),
              );
            },
          },
        );
      }),
    [entityId, moveFilesMutation, enqueueSnackbar, invalidateFilesQueries],
  );

  const { mutate: renameFileMutation, isPending: isRenamingFile } =
    useGraphQLMutation(renameFileMutationDocument);

  const handleRenameFile = useCallback(
    ({
      fileId,
      newOriginalName,
    }: Readonly<{ fileId: Id; newOriginalName: string }>) =>
      new Promise<void>((resolve, reject) => {
        renameFileMutation(
          { entityId: entityId ?? EMPTY_ID, fileId, newOriginalName },
          {
            onSuccess: () => {
              enqueueSnackbar('Fichier renommé.');
              invalidateFilesQueries();
              resolve();
            },
            onError: (mutationError: unknown) => {
              reject(
                mutationError instanceof Error
                  ? mutationError
                  : new Error('Une erreur est survenue.'),
              );
            },
          },
        );
      }),
    [entityId, renameFileMutation, enqueueSnackbar, invalidateFilesQueries],
  );

  return {
    allowedFileCategories,
    fileCategoryAccesses,
    files,
    fileCountsByCategoryId,
    isLoadingFileDirectory,
    isDeletingFiles,
    isMovingFiles,
    isRenamingFile,
    queryDocumentNamesToInvalidate,
    handleDeleteFiles,
    handleMoveFiles,
    handleRenameFile,
  };
}
